import { useTranslation } from 'react-i18next';
import { articleDetails, articleComments } from '@/mocks/articleData';
import { allArticles } from '@/mocks/categoryData';

interface CommentSectionProps {
  articleId: number;
}

export default function CommentSection({ articleId }: CommentSectionProps) {
  const { t } = useTranslation();
  const comments = articleComments[articleId] || [];

  return (
    <section className="mt-12 pt-10 border-t border-background-200/70">
      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
        {t('article.comments.title')}
      </h2>
      {comments.length > 0 && (
        <p className="text-sm text-foreground-400 mb-8">
          {comments.length} {t('article.comments.count')}
        </p>
      )}

      {comments.length > 0 ? (
        <div className="flex flex-col gap-5">
          {comments.map((comment) => (
            <div key={comment.id} className="bg-background-50 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-secondary-100 overflow-hidden flex-shrink-0">
                  <img
                    src={comment.avatar}
                    alt={comment.author}
                    className="w-full h-full object-cover"
                    title={comment.author}
                  />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm text-foreground-900">{comment.author}</div>
                  <div className="text-xs text-foreground-400">{comment.date}</div>
                </div>
                <button className="inline-flex items-center gap-1 text-xs text-foreground-400 hover:text-accent-500 cursor-pointer transition-colors duration-200 whitespace-nowrap">
                  <i className="ri-thumb-up-line text-sm w-4 h-4 flex items-center justify-center"></i>
                  <span>{comment.likes > 999 ? `${(comment.likes / 1000).toFixed(1)}k` : comment.likes}</span>
                </button>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <i className="ri-chat-3-line text-4xl text-foreground-300 mb-3 block"></i>
          <p className="text-sm text-foreground-400">{t('article.comments.empty')}</p>
        </div>
      )}

      <div className="mt-8 bg-background-100 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-foreground-800 mb-4">发表评论</h3>
        <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              name="comment_author"
              placeholder={t('article.comments.namePlaceholder')}
              className="flex-1 rounded-md px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors duration-200"
            />
            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-protection-field"
            />
          </div>
          <textarea
            name="comment_content"
            rows={3}
            maxLength={500}
            placeholder={t('article.comments.contentPlaceholder')}
            className="rounded-md px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors duration-200 resize-none"
          ></textarea>
          <button
            type="submit"
            className="self-end inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-300"
          >
            <i className="ri-send-plane-line text-sm w-4 h-4 flex items-center justify-center"></i>
            {t('article.comments.submit')}
          </button>
        </form>
      </div>
    </section>
  );
}